import type { FormDraftCardEnvelope, FormDraftField } from "@/components/generative-ui/envelope/types";
import type { ModuleType, PermissionType } from "@/contexts/permission-context";

export type FormCapability = "confirm_patient" | "confirm_patient_update" | "confirm_appointment" | "confirm_stock_item";
export class FormPreparationError extends Error {}
type PatientPayload = {
  name: string | null; cpf: string | null; birthday: string | null; gender: "M" | "F" | null; phone: string | null; email: string | null;
  // WEB-FORMSET-01: sent only when the card carries the guardian fields.
  name_of_person_responsible?: string | null; cpf_of_the_person_responsible?: string | null;
};
export type OwnerFormPayload = PatientPayload | (PatientPayload & { patient_id: string }) |
  { patientsId: string; proceduresId: string; employeesId: string; date: string; hour: string[]; value: number; return: boolean; comments: null } |
  { name: string; description: null; category_id: null; cost_price: number; sale_price: number; current_quantity: number; min_quantity: number; unit: string };

export const FORM_PERMISSIONS: Record<FormCapability, [ModuleType, PermissionType]> = {
  confirm_patient: ["patient", "create patient"], confirm_patient_update: ["patient", "update patient"],
  confirm_appointment: ["scheduling", "create scheduling"], confirm_stock_item: ["stock item", "create stock item"],
};

export function formCapability(action: string): FormCapability | null {
  return Object.prototype.hasOwnProperty.call(FORM_PERMISSIONS, action) ? action as FormCapability : null;
}

export function number(value: unknown): number {
  const normalized = typeof value === "string" ? value.trim().replace(",", ".") : value;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) throw new FormPreparationError("Confira os valores numéricos do formulário.");
  return parsed;
}

export function id(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) throw new FormPreparationError("Não foi possível confirmar os registros selecionados. Prepare um novo rascunho.");
  return value;
}

/** Card keys a patient review may carry, the guardian pair included (contracts/copilot/form-sets/v1/field_map.json). */
export const PATIENT_CARD_KEYS: readonly string[] = ["name", "cpf", "birthday", "gender", "phone", "email", "responsible_name", "responsible_cpf"];

/** A reviewed card value, trimmed; null when empty or not text. */
export function cardText(values: Record<string, unknown>, key: string): string | null {
  const value = values[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** The six base patient keys, shared by the single and the set route: no unknown card key, sex M or F. */
export function reviewedPatient(values: Record<string, unknown>): PatientPayload {
  if (Object.keys(values).some((key) => !PATIENT_CARD_KEYS.includes(key))) throw new FormPreparationError("Este rascunho contém campos sem suporte nesta versão. Prepare um novo rascunho.");
  const gender = cardText(values, "gender");
  if (gender !== null && gender !== "M" && gender !== "F") throw new FormPreparationError("Confira o campo sexo.");
  const text = (key: string) => cardText(values, key);
  return { name: text("name"), cpf: text("cpf"), birthday: text("birthday"), gender, phone: text("phone"), email: text("email") };
}

/** The full human review includes defaults which used to be invisible. */
export function reviewCard(card: FormDraftCardEnvelope, data: Record<string, unknown>): FormDraftCardEnvelope {
  const extra: FormDraftField[] = [];
  const field = (key: string, label: string, value: unknown) => {
    if (!card.fields.some((item) => item.key === key)) extra.push({ key, label, value: String(value ?? 0), type: "text", editable: true, required: true, source: "system" });
  };
  if (card.action === "confirm_patient" || card.action === "confirm_patient_update") {
    const patientFields: Array<[string, string, FormDraftField["type"]]> = [
      ["name", "Nome", "text"], ["cpf", "CPF", "cpf"], ["birthday", "Data de nascimento", "date"],
      ["gender", "Sexo", "select"], ["phone", "Telefone", "phone"], ["email", "E-mail", "email"],
    ];
    for (const [key, label, type] of patientFields) if (!card.fields.some((item) => item.key === key)) {
      extra.push({ key, label, type, value: typeof data[key] === "string" ? data[key] as string : null,
        required: key === "name" && card.action === "confirm_patient", editable: true, source: "system", options: key === "gender" ? ["M", "F"] : null });
    }
  }
  if (card.action === "confirm_appointment") field("value", "Valor do agendamento (R$)", data.procedure_value ?? 0);
  if (card.action === "confirm_stock_item") {
    field("cost_price", "Preço de custo (R$)", 0);
    field("sale_price", "Preço de venda (R$)", 0);
  }
  return { ...card, confirm_label: "Revisar e salvar", fields: [...card.fields, ...extra] };
}

export function ownerFormPayload(capability: FormCapability, data: Record<string, unknown>, values: Record<string, unknown>): OwnerFormPayload {
  const text = (key: string) => cardText(values, key);
  if (capability === "confirm_patient" || capability === "confirm_patient_update") {
    const payload = reviewedPatient(values);
    if ("responsible_name" in values || "responsible_cpf" in values) {
      payload.name_of_person_responsible = text("responsible_name");
      payload.cpf_of_the_person_responsible = text("responsible_cpf");
    }
    return capability === "confirm_patient_update" ? { ...payload, patient_id: id(data.patient_id) } : payload;
  }
  if (capability === "confirm_appointment") {
    const professional = text("professional");
    const options = data.professional_ids_by_name;
    const selected = options && typeof options === "object" && !Array.isArray(options)
      ? (options as Record<string, unknown>)[professional ?? ""] : undefined;
    const employee = selected ?? (professional === data.professional ? data.employee_id : undefined);
    if (text("procedure") !== data.procedure) throw new FormPreparationError("Prepare um novo rascunho para alterar o procedimento.");
    return { patientsId: id(data.patient_id), proceduresId: id(data.procedure_id), employeesId: id(employee),
      date: text("date") ?? "", hour: [text("time") ?? ""], value: number(values.value), return: false, comments: null };
  }
  return { name: text("name") ?? "", description: null, category_id: null,
    cost_price: number(values.cost_price), sale_price: number(values.sale_price), current_quantity: number(values.quantity),
    min_quantity: number(values.min_threshold ?? 0), unit: text("unit") ?? "un" };
}
